import {
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    VStack,
    Flex,
    Icon,
    Heading,
    Button,
    Link as ChakraLink,
} from '@chakra-ui/react';
import { FaClipboardList } from 'react-icons/fa';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { AllOrdersEntry } from '../../hooks/useGetOrdersFeed';
import { useArchiveOrder } from '../../hooks/useArchiveOrder';
import SortableTableHeader, { SortState, DashboardSortField } from './components/SortableTableHeader';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import { Vin, NoteCountBadge, SupplierCommunicationCountBadge, EnterEstimateBadge } from './components';
import { useColumnWidths } from '../../hooks/useColumnWidths';
import ResizableTh from '../ResizableTh';
import { STICKY_TABLE_HEADER_Z_INDEX } from '../../utils/tableCopySx';
import {
    DashboardRoColumnRow,
    DashboardRoSelectAllHeader,
    type DashboardBulkSelection,
    getDashboardBulkTableSx,
    getDashboardRoColumnPl,
    getDashboardRowHighlight,
    handleOrderBulkRowClick,
} from './dashboardBulkSelection';

interface ArchivedOrdersTableProps {
    orders: Array<AllOrdersEntry>;
    sortState?: SortState;
    onSortChange?: (field: DashboardSortField) => void;
    bulkSelection?: DashboardBulkSelection;
}

const ArchivedOrdersTable = ({ orders, sortState, onSortChange, bulkSelection }: ArchivedOrdersTableProps) => {
    const navigate = useNavigate();
    const archiveOrder = useArchiveOrder();
    const { widths: colWidths, updateWidth: updateColWidth } = useColumnWidths('dashboard_archived');

    const sessionData = useContext(SessionContext);
    const canArchive = !!sessionData?.isAuthenticated && sessionData.permissions.includes('archive-unarchive');

    const handleUnarchiveOrder = (orderId: string) => {
        archiveOrder.mutate({ orderId, archived: false });
    };

    const defaultSortState: SortState = { field: null, direction: 'asc' };
    const currentSortState = sortState || defaultSortState;

    if (!orders || orders.length === 0) {
        return (
            <Flex
                direction="column"
                align="center"
                justify="center"
                py={12}
                px={6}
                textAlign="center"
            >
                <Icon
                    as={FaClipboardList as React.ElementType}
                    w={16}
                    h={16}
                    color="gray.300"
                    mb={6}
                />
                <Heading
                    size="lg"
                    color="gray.600"
                    mb={2}
                >
                    No archived orders
                </Heading>
                <Text
                    color="gray.500"
                    fontSize="md"
                    maxW="md"
                >
                    Archived orders will appear here
                </Text>
            </Flex>
        );
    }

    return (
        <Table variant="simple" size='sm' minW="100%" sx={getDashboardBulkTableSx(bulkSelection, Object.keys(colWidths).length > 0 ? { tableLayout: 'fixed', '& td': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } } : undefined)}>
            <Thead position="sticky" top={0} zIndex={STICKY_TABLE_HEADER_Z_INDEX}>
                <Tr bg="gray.50">
                    <ResizableTh columnKey="roNumber" storedWidth={colWidths.roNumber} onResize={updateColWidth} minWidth={100} py={2.5} pl={getDashboardRoColumnPl(bulkSelection)} pr={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200" position="relative">
                        <DashboardRoSelectAllHeader bulkSelection={bulkSelection} />
                        RO Number
                    </ResizableTh>
                    <ResizableTh columnKey="vehicle" storedWidth={colWidths.vehicle} onResize={updateColWidth} minWidth={190} py={2.5} px={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Vehicle</ResizableTh>
                    <ResizableTh columnKey="customer" storedWidth={colWidths.customer} onResize={updateColWidth} minWidth={120} py={2.5} px={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Customer</ResizableTh>
                    <ResizableTh columnKey="location" storedWidth={colWidths.location} onResize={updateColWidth} minWidth={140} py={2.5} px={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Location</ResizableTh>
                    <ResizableTh columnKey="partsOrdered" storedWidth={colWidths.partsOrdered} onResize={updateColWidth} minWidth={120} py={2.5} px={3} borderBottomWidth="1px" borderColor="gray.200">
                        {onSortChange ? (
                            <SortableTableHeader
                                label="PARTS ORDERED"
                                field="partsOrdered"
                                sortState={currentSortState}
                                onSort={onSortChange}
                            />
                        ) : (
                            <Text color="gray.500" fontSize="11px" fontWeight="semibold" textAlign="center">
                                Parts Ordered
                            </Text>
                        )}
                    </ResizableTh>
                    {canArchive && <Th py={2.5} px={3} textAlign="center" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200" w="120px"></Th>}
                </Tr>
            </Thead>
            <Tbody>
                {orders.map((order, rowIndex) => {
                    const isSelected = bulkSelection?.selectedOrderIds.has(order._id) ?? false;

                    return (
                    <Tr
                        key={order._id}
                        {...getDashboardRowHighlight(isSelected, false)}
                        onClick={(e) => {
                            handleOrderBulkRowClick(
                                e,
                                order._id,
                                rowIndex,
                                (id, idx, shift) => bulkSelection?.onToggle(id, idx, shift),
                                () => navigate(`/order/${order._id}`, { state: { source: 'dashboard' } }),
                                !!bulkSelection?.enabled,
                            );
                        }}
                    >
                        <Td
                            py={3}
                            pl={getDashboardRoColumnPl(bulkSelection)}
                            pr={3}
                            position={bulkSelection?.enabled ? 'relative' : undefined}
                            whiteSpace="normal"
                            wordBreak="break-word"
                            borderBottomWidth="1px"
                            borderColor="gray.100"
                        >
                            <DashboardRoColumnRow
                                bulkSelection={bulkSelection}
                                orderId={order._id}
                                rowIndex={rowIndex}
                                isChecked={isSelected}
                            >
                                <EnterEstimateBadge canEnter={order.canEnterEstimate} />
                                <ChakraLink
                                    as={RouterLink}
                                    to={`/order/${order._id}`}
                                    state={{ source: 'dashboard' }}
                                    onClick={(e) => e.stopPropagation()}
                                    fontSize="sm"
                                    fontWeight="medium"
                                    color="gray.800"
                                    _hover={{ textDecoration: 'underline' }}
                                >
                                    {order.roNumber}
                                </ChakraLink>
                                <NoteCountBadge noteCount={order.noteCount} orderId={order._id} />
                                <SupplierCommunicationCountBadge count={order.supplierCommunicationCount} />
                            </DashboardRoColumnRow>
                        </Td>
                        <Td py={3} px={3} whiteSpace="normal" wordBreak="break-word" borderBottomWidth="1px" borderColor="gray.100">
                            <VStack align="start" spacing={0.5}>
                                <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                    {order.ymm}
                                </Text>
                                <Vin vin={order.vin} />
                            </VStack>
                        </Td>
                        <Td py={3} px={3} whiteSpace="normal" wordBreak="break-word" minW="120px" borderBottomWidth="1px" borderColor="gray.100">
                            {order.lastName && order.firstName ?
                                <VStack align="start" spacing={0}>
                                    <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                        {order.lastName || '—'}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                        {order.firstName || '—'}
                                    </Text>
                                </VStack> :
                                <Text fontSize="sm" color="gray.500">
                                    —
                                </Text>
                            }
                        </Td>
                        <Td py={3} px={3} whiteSpace="normal" wordBreak="break-word" minW="140px" borderBottomWidth="1px" borderColor="gray.100">
                            <Text fontSize="sm" color="gray.700">
                                {order.locationName || '—'}
                            </Text>
                        </Td>
                        <Td py={3} px={3} whiteSpace="normal" textAlign="center" borderBottomWidth="1px" borderColor="gray.100">
                            <Text fontSize="sm" color="gray.700">
                                {order.partsOrdered}/{order.partsTotal}
                            </Text>
                        </Td>
                        {canArchive && <Td py={3} px={3} textAlign="center" minW="120px" borderBottomWidth="1px" borderColor="gray.100">
                            <Button
                                size="sm"
                                colorScheme="blue"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnarchiveOrder(order._id);
                                }}
                            >
                                Unarchive
                            </Button>
                        </Td>}
                    </Tr>
                    );
                })}
            </Tbody>
        </Table>
    );
};

export default ArchivedOrdersTable;
